import * as React from "react";

import { type TopicFieldRow } from "@/shared/domain/sessions";
import {
  CanonicalDataType,
  type SchemaFieldRecord,
} from "@/shared/domain/topics";
import {
  canonicalTypeTone,
  ChevronDown,
  ChevronRight,
} from "@/shared/foundation/icons";
import { pluralize } from "@/shared/text";

import { PendingStat } from "./PendingStat";
import styles from "./TopicFieldStatsTable.module.css";
import { useGridColumnLeft } from "./useGridColumnLeft";

/** The topics table's Schema column, which the Type column lines up under without stats. */
const SCHEMA_COLUMN_ID = "schema";

/** The number stat columns, in order. */
const NUMBER_COLUMNS = [
  { stat: "min", label: "Min" },
  { stat: "max", label: "Max" },
  { stat: "mean", label: "Mean" },
  { stat: "median", label: "Median" },
  { stat: "stddev", label: "Std dev" },
] as const;

/** The boolean stat columns, shown only when some field of the topic has them. */
const BOOLEAN_COLUMNS = [
  { stat: "true_count", label: "True" },
  { stat: "false_count", label: "False" },
] as const;

type StatColumn =
  (typeof NUMBER_COLUMNS)[number] | (typeof BOOLEAN_COLUMNS)[number];

const COLUMN_KIND: Record<StatColumn["stat"], "numeric" | "boolean"> = {
  min: "numeric",
  max: "numeric",
  mean: "numeric",
  median: "numeric",
  stddev: "numeric",
  true_count: "boolean",
  false_count: "boolean",
};

// The backend's precision is kept: no rounding beyond what a double holds.
const statFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 20,
});

const ARRAY_NOTE = "Stats aren’t computed for fields inside arrays";

const pathKey = (path: readonly string[]) => JSON.stringify(path);

function isArrayField(field: SchemaFieldRecord): boolean {
  return (
    field.canonical_data_type === CanonicalDataType.Array ||
    field.canonical_data_type === CanonicalDataType.NumberArray ||
    field.data_type.endsWith("]")
  );
}

/**
 * One line of the table, at its depth in the schema: a field with the lines
 * directly under it, or an element of a named array.
 */
interface FieldNode {
  key: string;
  path: readonly string[];
  name: string;
  /** An element's position in its array, shown beside its name; `null` for a field. */
  position: number | null;
  dataType: string;
  canonicalDataType: CanonicalDataType;
  plan: TopicFieldRow["plan"];
  stats: TopicFieldRow["stats"];
  isArray: boolean;
  /** Whether the field is an array whose elements are named, each with stats of its own. */
  hasNamedElements: boolean;
  depth: number;
  children: FieldNode[];
  /** Whether an array holds this line, at any depth. */
  insideArray: boolean;
}

function fieldNode(row: TopicFieldRow): FieldNode {
  const { field } = row;
  const path = field.path_in_schema;
  const depth = path.length - 1;
  return {
    key: pathKey(path),
    path,
    name: path[depth],
    position: null,
    dataType: field.data_type,
    canonicalDataType: field.canonical_data_type,
    plan: row.plan,
    stats: row.stats,
    isArray: isArrayField(field),
    hasNamedElements: row.elements.length > 0,
    depth,
    children: row.elements.map((element) => ({
      key: pathKey(element.path),
      path: element.path,
      name: element.name,
      position: element.position,
      dataType: element.dataType,
      canonicalDataType: element.canonicalDataType,
      plan: element.plan,
      stats: element.stats,
      isArray: false,
      hasNamedElements: false,
      depth: depth + 1,
      children: [],
      insideArray: false,
    })),
    insideArray: false,
  };
}

/**
 * The fields as a tree, each parent's children in the order the schema lists
 * them, and a named array's elements in element order. A field whose parent
 * the schema does not list sits at the top.
 */
function fieldTree(rows: readonly TopicFieldRow[]): FieldNode[] {
  const nodes = new Map<string, FieldNode>();
  for (const row of rows) {
    const node = fieldNode(row);
    nodes.set(node.key, node);
  }
  const roots: FieldNode[] = [];
  for (const node of nodes.values()) {
    const parent =
      node.path.length > 1
        ? nodes.get(pathKey(node.path.slice(0, -1)))
        : undefined;
    if (parent === undefined) {
      roots.push(node);
    } else {
      parent.children.push(node);
    }
  }
  const markInsideArrays = (node: FieldNode, inside: boolean) => {
    node.insideArray = inside;
    const holds = inside || node.isArray;
    node.children.forEach((child) => markInsideArrays(child, holds));
  };
  roots.forEach((root) => markInsideArrays(root, false));
  return roots;
}

/** Arrays start collapsed, except a named array, whose elements have stats; objects start expanded. */
function initiallyCollapsed(roots: readonly FieldNode[]): Set<string> {
  const collapsed = new Set<string>();
  const visit = (node: FieldNode) => {
    if (node.children.length > 0 && node.isArray && !node.hasNamedElements) {
      collapsed.add(node.key);
    }
    node.children.forEach(visit);
  };
  roots.forEach(visit);
  return collapsed;
}

/** The fields to draw, in order, leaving out those under a collapsed field. */
function visibleNodes(
  roots: readonly FieldNode[],
  collapsed: ReadonlySet<string>,
): FieldNode[] {
  const out: FieldNode[] = [];
  const visit = (node: FieldNode) => {
    out.push(node);
    if (!collapsed.has(node.key)) {
      node.children.forEach(visit);
    }
  };
  roots.forEach(visit);
  return out;
}

export interface TopicFieldStatsTableProps {
  rows: readonly TopicFieldRow[];
  /** Whether the stat columns are shown; without them the table lists the fields and their types. */
  statsShown: boolean;
  /** Whether a topic stats run is under way: fields it will give stats show placeholders. */
  generating: boolean;
}

/**
 * The fields of one topic schema as a tree, one line each: the field's name,
 * its types, and the stats the session's data gives it in fixed columns: min,
 * max, mean, median and standard deviation for a number, and true and false
 * counts for a boolean when the topic has any. Arrays start collapsed and
 * objects expanded. A named array lists its elements under it, each with its
 * position and stats, and starts expanded. Only a top-level array without
 * named elements explains why it has no stats; every other field without stats
 * leaves its cells blank. While a run is under way, placeholders stand where
 * its values will appear. A line above counts the schema's fields and, once
 * the stats are out, how many have them; a named array counts as one field,
 * with stats when any of its elements has them.
 *
 * Without stats the table has only Field and Type, and lines up with the
 * topics table it is expanded in: Type starts where that table's Schema column
 * does, wherever the user has sized it, and its text starts where Schema's
 * does. With the Schema column hidden, Type keeps its fixed width at the right.
 */
export function TopicFieldStatsTable({
  rows,
  statsShown,
  generating,
}: TopicFieldStatsTableProps) {
  const roots = React.useMemo(() => fieldTree(rows), [rows]);
  const [table, setTable] = React.useState<HTMLTableElement | null>(null);
  const schemaLeft = useGridColumnLeft(table, SCHEMA_COLUMN_ID, !statsShown);
  const aligned = !statsShown && schemaLeft !== null;
  const [collapsed, setCollapsed] = React.useState(() =>
    initiallyCollapsed(roots),
  );
  const toggle = (key: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (!next.delete(key)) {
        next.add(key);
      }
      return next;
    });

  const hasBooleanStats = rows.some(
    (row) => row.plan.kind === "boolean" && row.stats !== null,
  );
  const statColumns: StatColumn[] = statsShown
    ? [...NUMBER_COLUMNS, ...(hasBooleanStats ? BOOLEAN_COLUMNS : [])]
    : [];
  const withStats = rows.filter(
    (row) => hasStats(row) || row.elements.some(hasStats),
  ).length;
  const countOnlyTopic = rows.some(
    (row) => row.plan.skipReason === "count_only_topic",
  );

  return (
    <div className={styles.root}>
      <p className={styles.summary}>
        <span>
          <b className={styles.count}>{rows.length.toLocaleString()}</b>{" "}
          {pluralize("field", rows.length)}
        </span>
        {statsShown && !generating && !countOnlyTopic && (
          <Separated>
            <b className={styles.count}>{withStats.toLocaleString()}</b> with
            stats
          </Separated>
        )}
        {statsShown && countOnlyTopic && (
          <Separated>No stats for image or point-cloud topics</Separated>
        )}
      </p>
      <div className={styles.scroll}>
        <table
          ref={setTable}
          aria-label="Fields"
          className={styles.table}
          data-stats={statColumns.length > 0 ? "" : undefined}
          data-aligned={aligned ? "" : undefined}
        >
          <colgroup>
            <col style={aligned ? { width: schemaLeft } : undefined} />
            <col className={aligned ? undefined : styles.typeColumn} />
            {statColumns.map(({ stat }) => (
              <col key={stat} className={styles.statColumn} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className={styles.head}>Field</th>
              <th className={styles.head}>Type</th>
              {statColumns.map(({ stat, label }) => (
                <th key={stat} className={styles.headNumber}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleNodes(roots, collapsed).map((node) => (
              <tr key={node.key} className={styles.row}>
                <FieldCell
                  node={node}
                  expanded={!collapsed.has(node.key)}
                  onToggle={() => toggle(node.key)}
                />
                <TypeCell
                  dataType={node.dataType}
                  canonicalDataType={node.canonicalDataType}
                />
                {statColumns.length > 0 && (
                  <StatCells
                    node={node}
                    columns={statColumns}
                    generating={generating}
                  />
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function hasStats({ plan, stats }: Pick<TopicFieldRow, "plan" | "stats">) {
  return plan.skipReason === null && stats !== null;
}

function Separated({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span className={styles.separator} aria-hidden>
        ·
      </span>
      <span>{children}</span>
    </>
  );
}

/** Indent per level of nesting, and where the first level's guide line sits. */
const INDENT_PX = 22;
const BASE_PX = 8;

/**
 * The field's name, indented by its depth with a guide line per level, a
 * chevron that folds the fields under it with their count beside the name,
 * and the full path on hover. An element shows its position in the array
 * beside its name.
 */
function FieldCell({
  node,
  expanded,
  onToggle,
}: {
  node: FieldNode;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { path, name, position } = node;
  const hasChildren = node.children.length > 0;
  return (
    <th
      scope="row"
      className={styles.fieldCell}
      style={{ paddingLeft: BASE_PX + node.depth * INDENT_PX }}
    >
      {Array.from({ length: node.depth }, (_, level) => (
        <span
          key={level}
          className={styles.guide}
          style={{ left: BASE_PX + level * INDENT_PX + 7 }}
          aria-hidden
        />
      ))}
      <span className={styles.field}>
        {hasChildren ? (
          <button
            type="button"
            className={styles.chevron}
            aria-expanded={expanded}
            aria-label={`${expanded ? "Collapse" : "Expand"} ${name}`}
            onClick={onToggle}
          >
            {expanded ? (
              <ChevronDown aria-hidden />
            ) : (
              <ChevronRight aria-hidden />
            )}
          </button>
        ) : (
          <span className={styles.chevronSpacer} />
        )}
        <span className={styles.name} title={path.join(".")}>
          {name}
        </span>
        {position !== null && (
          <span className={styles.elementPosition}>
            <span aria-hidden>#{position}</span>
            <span className="sr-only">position {position}</span>
          </span>
        )}
        {hasChildren && (
          <span className={styles.childCount}>
            {node.children.length.toLocaleString()}
          </span>
        )}
      </span>
    </th>
  );
}

/** The line's raw type, then a dot in its canonical type's tone and the canonical type. */
function TypeCell({
  dataType,
  canonicalDataType,
}: {
  dataType: string;
  canonicalDataType: CanonicalDataType;
}) {
  return (
    <td className={styles.typeCell}>
      <span className={styles.types}>
        <span className={styles.rawType}>{dataType}</span>
        <span className={styles.canonicalType}>
          <span
            className={styles.dot}
            style={{
              backgroundColor: canonicalTypeTone(canonicalDataType),
            }}
            aria-hidden
          />
          {canonicalDataType}
        </span>
      </span>
    </td>
  );
}

/**
 * A line's stat cells: the note on a top-level array without named elements,
 * placeholders where an under-way run will publish values, the published
 * values, or blanks.
 */
function StatCells({
  node,
  columns,
  generating,
}: {
  node: FieldNode;
  columns: readonly StatColumn[];
  generating: boolean;
}) {
  const { plan, stats } = node;
  if (
    plan.skipReason === "array" &&
    !node.insideArray &&
    !node.hasNamedElements
  ) {
    return (
      <td colSpan={columns.length} className={styles.note}>
        {ARRAY_NOTE}
      </td>
    );
  }
  if (plan.kind === null || (!generating && stats === null)) {
    return <td colSpan={columns.length} className={styles.blank} />;
  }
  return (
    <>
      {columns.map(({ stat }) => {
        const applies = COLUMN_KIND[stat] === plan.kind;
        const value = stats?.[stat];
        let content: React.ReactNode = null;
        let text: string | undefined;
        if (applies && generating) {
          content = <PendingStat />;
        } else if (applies && value !== undefined) {
          text = statFormat.format(value);
          content = text;
        }
        // A value at full precision can outgrow its column; hovering shows it whole.
        return (
          <td key={stat} className={styles.number} title={text}>
            {content}
          </td>
        );
      })}
    </>
  );
}
